import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Typography, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";

interface FilePreview {
  id?: number;
  name: string;
  preview: string;
  file: File | null;
  isExisting?: boolean;
  size: number;
  error?: string;
}

interface Props {
  value: FilePreview[];
  onChange: (files: FilePreview[]) => void;
  error?: string;
  isEditMode?: boolean;
}

const DropzoneUploader: React.FC<Props> = ({
  value,
  onChange,
  error,
  isEditMode = false,
}) => {
  const maxFiles = isEditMode ? 1 : 5;
  const isDisabled = value.length >= maxFiles;
  const [fileError, setFileError] = React.useState<string>("");
  const acceptedExtensions = [
    "pdf",
    "docx",
    "xlsx",
    "png",
    "jpg",
    "jpeg",
    "mp4",
    "txt",
    "csv",
    "doc",
  ];
  const MAX_FILE_SIZE_MB = 110;
  const MAX_FILE_COUNT = 5;
  const MAX_FILE_ERROR_KEY = "__max_limit__";
  const isSupportedFile = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    return acceptedExtensions.includes(ext || "");
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (isDisabled) return;

      const newFiles: FilePreview[] = acceptedFiles.map((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        const sizeMB = file.size / (1024 * 1024);

        let error = "";
        if (!ext || !acceptedExtensions.includes(ext)) {
          error = `File "${file.name}" is not a supported file type.`;
        } else if (sizeMB > MAX_FILE_SIZE_MB) {
          error = `File "${file.name}" exceeds the 100MB size limit.`;
        }

        return {
          name: file.name,
          size: file.size,
          file,
          preview: URL.createObjectURL(file),
          isExisting: false,
          error,
        };
      });

      const totalFiles = [...value, ...newFiles];
      if (totalFiles.length > 5) return;

      setFileError(""); // clear any global error
      onChange(totalFiles);
    },
    [value, onChange, isDisabled]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isDisabled,
  });

  const handleRemove = (index: number) => {
    const updated = [...value];
    updated.splice(index, 1);
    onChange(updated);
  };

  return (
    <>
      <Box
        {...getRootProps()}
        sx={{
          border: "1px dotted #AFAFAF",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          color: isDisabled ? "text.disabled" : "text.secondary",
          cursor: isDisabled ? "not-allowed" : "pointer",
          marginTop: "20px",
          height: "50px",
          width: "900px",
          backgroundColor: isDisabled ? "#f5f5f5" : "transparent",
        }}
      >
        <input {...getInputProps()} />
        <Typography>
          {isDisabled
            ? `You can upload maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""}`
            : isDragActive
              ? "Drop the files here..."
              : "Click here or drop files here to attach."}
        </Typography>
      </Box>
      {(error || fileError) && (
        <Typography color="error" fontSize={14} mt={1}>
          {error || fileError}
        </Typography>
      )}
      <Box mt={2}>
        {value.map((file, index) => (
          <Box
            key={`${file.name}-${index}`}
            display="flex"
            flexDirection="column"
            sx={{
              border: "1px solid #ccc",
              borderRadius: 1,
              p: 1,
              mb: 1,
              width: "550px",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography fontSize={14}>
                {file.isExisting ? (
                  <a
                    href={file.preview}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {file.name}
                  </a>
                ) : (
                  `${file.name}`
                )}{" "}
                ({(file.size / 1024).toFixed(1)} KB)
              </Typography>
              <IconButton onClick={() => handleRemove(index)}>
                <Close />
              </IconButton>
            </Box>

            {/* Show per-file error if present */}
            {file.error && (
              <Typography color="error" fontSize={12} mt={0.5}>
                {file.error}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </>
  );
};

export default DropzoneUploader;
